
import React, { useState, useEffect, useRef } from 'react';
import { Box, TextField, FormControl, FormLabel, RadioGroup, FormControlLabel, Radio, Typography, Button } from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import axios from 'axios';
import SaveIcon from '@mui/icons-material/Save';

const Alert = MuiAlert;

const AgentName = ({ agentId, agentData, onSave, onCancel, onAgentCreated }) => {
  const [agentName, setAgentName] = useState('');
  const [agentDescription, setAgentDescription] = useState('');
  const [agentDomain, setAgentDomain] = useState('');
  const [nameError, setNameError] = useState('');
  const [domainError, setDomainError] = useState(false);
  const [saveError, setSaveError] = useState('');
  const agentNameRef = useRef(null);

  // Populate form fields when agentData is provided
  useEffect(() => {
    if (agentData && agentData.id) {
      console.log('Populating form with agent data:', agentData);
      setAgentName(agentData.agentName || '');
      setAgentDescription(agentData.description || '');
      setAgentDomain(agentData.agentDomain || '');
    }
  }, [agentData?.id]); // Only run when agentData.id changes, not the entire object

  // Focus the agent name field when component mounts
  useEffect(() => {
    if (agentNameRef.current) {
      agentNameRef.current.focus();
    }
  }, []);
  const validateDomain = (domain) => {
    if (!domain.trim()) return true;
    const urlPattern = /^https?:\/\/.+/;
    const localhostPattern = /^https?:\/\/localhost:\d+/;
    return urlPattern.test(domain) || localhostPattern.test(domain);
  };

  const checkAgentExists = async (name) => {
    if (!name.trim()) {
      setNameError('');
      return;
    }

    try {
      const response = await axios.post('/agents/agentExists', {
        agentName: name,
        id: agentId || null
      });

      if (response.data.exists) {
        setNameError('Agent name already exists');
      } else {
        setNameError('');
      }
    } catch (error) {
      console.error('Error checking agent name:', error);
    }
  };

  const handleDomainChange = (e) => {
    const value = e.target.value;
    setAgentDomain(value);

    // Validate on blur and input
    if (value.trim() && !validateDomain(value)) {
      setDomainError(true);
    } else {
      setDomainError(false);
    }
  };

  const handleDomainBlur = (e) => {
    const value = e.target.value;
    if (value.trim() && !validateDomain(value)) {
      setDomainError(true);
    } else {
      setDomainError(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaveError('');
      const agentObject = {
        id: agentId || undefined, // Include ID if updating existing agent
        agentName: agentName,
        description: agentDescription,
        agentDomain: agentDomain,
        active: true,
      };

      const response = await axios.post('/agents/saveAgent', agentObject);
      if (response.data && response.data.agentId) {
        // Call the callback with the created agent data
        if (onAgentCreated) {
          const updatedAgentObject = { ...agentObject, id: response.data.agentId };
          onAgentCreated(response.data.agentId, updatedAgentObject);
        }
      }
    } catch (error) {
      console.error('Error creating agent:', error);
      const d = error?.response?.data;
      const apiMessage = typeof d === 'string' ? d : d?.message;
      setSaveError(apiMessage || 'Failed to save agent. Please try again.');
    }
  };

  return (
    <Box sx={{ mt: 1, minHeight: '300px', textAlign: 'left' }}>
      Follow these steps to quickly set up the basics of your first Chattitude agent<br />
      You can always revisit and adjust these settings later.<br /><br />

      <Typography variant="body1" sx={{ mt: 2, mb: 1 }}>
        Give your agent a name. This name is customer-facing, so make it catchy or something
        your customers can relate to.
      </Typography>
      <TextField
        fullWidth
        label="Agent Name"
        variant="outlined"
        value={agentName}
        inputRef={agentNameRef}
        autoFocus
        onChange={(e) => {
          console.log('Agent name changed to:', e.target.value);
          setAgentName(e.target.value);
          setNameError('');
        }}
        onBlur={(e) => checkAgentExists(e.target.value)}
        margin="normal"
        required
        error={!!nameError}
        helperText={nameError || (nameError === false && !agentName.trim() ? "Agent Name is required" : "")}
      />
      <TextField
        fullWidth
        label="Agent Description"
        variant="outlined"
        value={agentDescription}
        onChange={(e) => setAgentDescription(e.target.value)}
        margin="normal"
        multiline
        rows={4}
        placeholder="Optional"
      />
      <Alert severity="info" sx={{ mt: 2 }}>
        <strong>Multilingual &amp; Voice Support:</strong> AI agents can understand and respond in dozens of languages, enabling natural conversations with users around the world. Agents can communicate through both text and voice, helping you create localized experiences, support global audiences, and engage customers in their preferred language—all from a single AI assistant.
      </Alert>
      {saveError && (
        <Typography variant="body2" color="error" sx={{ mt: 1 }}>
          {saveError}
        </Typography>
      )}

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 3 }}>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={!agentName.trim()}
          sx={{ minWidth: 120 }}
        >
          Save Your Agent
        </Button>
        <Button
          variant="outlined"
          onClick={onCancel}
          sx={{ minWidth: 120 }}
        >
          Cancel
        </Button>
      </Box>
    </Box>
  );
};

export default AgentName; 